import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Calendar,
  BarChart3,
  Clock,
  Sparkles,
  Image,
  FileText,
  Layers,
} from 'lucide-react';
import { api } from '../api/client';
import clsx from 'clsx';

// Format currency
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
}

// Format date
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Get icon for operation type
function getOperationIcon(operation: string) {
  switch (operation) {
    case 'brainstorm':
      return Sparkles;
    case 'design':
      return Image;
    case 'seo':
      return FileText;
    case 'mockup':
      return Layers;
    default:
      return BarChart3;
  }
}

// Progress bar component
function ProgressBar({
  value,
  max,
  color = 'bg-etsy-orange',
}: {
  value: number;
  max: number;
  color?: string;
}) {
  const percentage = Math.min(100, (value / max) * 100);
  const isWarning = percentage >= 80;
  const isDanger = percentage >= 95;

  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className={clsx(
          'h-2.5 rounded-full transition-all duration-300',
          isDanger ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : color
        )}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

export default function Costs() {
  // Fetch cost summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['costs-summary'],
    queryFn: () => api.costs.summary(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch per-listing costs
  const { data: perListing, isLoading: listingLoading } = useQuery({
    queryKey: ['costs-per-listing'],
    queryFn: () => api.costs.perListing({ limit: 10 }),
  });

  // Fetch chart data
  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['costs-chart'],
    queryFn: () => api.costs.chartData(14),
  });

  // Summary stats
  const stats = [
    {
      name: 'Today',
      value: formatCurrency(summary?.today.total || 0),
      count: summary?.today.count || 0,
      limit: summary?.budget.daily_limit || 10,
      spent: summary?.today.total || 0,
      icon: Calendar,
      color: 'bg-blue-500',
    },
    {
      name: 'This Week',
      value: formatCurrency(summary?.week.total || 0),
      count: summary?.week.count || 0,
      limit: summary?.budget.weekly_limit || 50,
      spent: summary?.week.total || 0,
      icon: TrendingUp,
      color: 'bg-green-500',
    },
    {
      name: 'This Month',
      value: formatCurrency(summary?.month.total || 0),
      count: summary?.month.count || 0,
      limit: summary?.budget.monthly_limit || 150,
      spent: summary?.month.total || 0,
      icon: BarChart3,
      color: 'bg-purple-500',
    },
    {
      name: 'All Time',
      value: formatCurrency(summary?.all_time.total || 0),
      count: summary?.all_time.count || 0,
      icon: DollarSign,
      color: 'bg-etsy-orange',
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Cost Dashboard</h1>
        <div className="text-sm text-gray-500">Updated every 30 seconds</div>
      </div>

      {/* Budget Alerts */}
      {summary?.budget.alerts && summary.budget.alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          {summary.budget.alerts.map((alert, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
            >
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
              <span className="text-yellow-800">{alert}</span>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center gap-4 mb-3">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.name}</p>
                <p className="text-2xl font-bold">{stat.value}</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-2">{stat.count} operations</p>
            {stat.limit && (
              <div className="mt-2">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Budget</span>
                  <span>
                    {formatCurrency(stat.spent)} / {formatCurrency(stat.limit)}
                  </span>
                </div>
                <ProgressBar value={stat.spent} max={stat.limit} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Cost by Operation */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Cost by Operation</h2>
          {summaryLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 bg-gray-200 rounded" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(summary?.by_operation || {}).map(([operation, data]) => {
                const Icon = getOperationIcon(operation);
                const total = Object.values(summary?.by_operation || {}).reduce(
                  (sum, d) => sum + d.total,
                  0
                );
                const percentage = total > 0 ? (data.total / total) * 100 : 0;

                return (
                  <div key={operation} className="flex items-center gap-4">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium capitalize">{operation}</span>
                        <span className="text-sm text-gray-500">
                          {formatCurrency(data.total)} ({data.count} ops)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-etsy-orange h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
              {Object.keys(summary?.by_operation || {}).length === 0 && (
                <p className="text-gray-500 text-center py-4">No operations recorded yet</p>
              )}
            </div>
          )}
        </div>

        {/* Cost by Model */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Cost by Model</h2>
          {summaryLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-gray-200 rounded" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(summary?.by_model || {}).map(([model, data]) => {
                const total = Object.values(summary?.by_model || {}).reduce(
                  (sum, d) => sum + d.total,
                  0
                );
                const percentage = total > 0 ? (data.total / total) * 100 : 0;

                return (
                  <div key={model}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-sm truncate max-w-[200px]">{model}</span>
                      <span className="text-sm text-gray-500">
                        {formatCurrency(data.total)} ({data.count})
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {Object.keys(summary?.by_model || {}).length === 0 && (
                <p className="text-gray-500 text-center py-4">No model usage recorded yet</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Daily Chart */}
      <div className="card mb-8">
        <h2 className="text-lg font-semibold mb-4">Daily Costs (Last 14 Days)</h2>
        {chartLoading ? (
          <div className="h-48 animate-pulse bg-gray-200 rounded" />
        ) : (
          <div className="flex items-end gap-1 h-48">
            {chartData?.daily_totals.map((day) => {
              const maxTotal = Math.max(...chartData.daily_totals.map((d) => d.total), 0.01);
              const height = (day.total / maxTotal) * 100;

              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-etsy-orange rounded-t transition-all duration-300 hover:bg-orange-600"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${day.date}: ${formatCurrency(day.total)} (${day.count} ops)`}
                  />
                  <span className="text-xs text-gray-500 transform -rotate-45 origin-left whitespace-nowrap">
                    {new Date(day.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              );
            })}
            {(!chartData?.daily_totals || chartData.daily_totals.length === 0) && (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                No data for this period
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per Listing Costs */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Cost per Listing</h2>
          {perListing?.stats && (
            <div className="text-sm text-gray-500">
              Avg: {formatCurrency(perListing.stats.average_cost_per_listing)} per listing
            </div>
          )}
        </div>

        {listingLoading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Listing</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">
                    Total Cost
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">
                    Operations
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Last Activity
                  </th>
                </tr>
              </thead>
              <tbody>
                {perListing?.listings.map((listing) => (
                  <tr
                    key={listing.listing_id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4">
                      <a
                        href={`/listing/${listing.listing_id}`}
                        className="text-etsy-orange hover:underline"
                      >
                        {listing.title}
                      </a>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={clsx(
                          'px-2 py-1 text-xs rounded-full',
                          listing.status === 'ready'
                            ? 'bg-green-100 text-green-800'
                            : listing.status === 'generating'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        )}
                      >
                        {listing.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-medium">
                      {formatCurrency(listing.total_cost)}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-500">
                      {listing.operation_count}
                    </td>
                    <td className="py-3 px-4 text-gray-500 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatDate(listing.last_activity)}
                    </td>
                  </tr>
                ))}
                {(!perListing?.listings || perListing.listings.length === 0) && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">
                      No listing costs recorded yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
